import { useState } from 'react';
import useSWR from 'swr';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Flex,
  Badge,
  Button,
  useToast,
} from '@chakra-ui/react';
import Loader from '../../components/Feedback/Loader';
import { MainContent, PageHeaderStructure } from '../../components/Layout';
import { AttentionModal } from '../../components/Modals';
import { fetchData } from '../../services/fetch';
import baseService from '../../services/base';

// EG-27 ("Não sou o admin"): solicitações de acesso recentes (pendentes, últimos
// 30 dias) p/ o admin ESG acompanhar e destravar leads bloqueados por admin antigo.
function formatDateTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}

// Cada ação exige DUPLA confirmação: o modal aparece 2x (step 1 → step 2 → executa).
const COPY = {
  approve: {
    endpoint: (id) => `/user/${id}/approve`,
    redType: false,
    success: 'Acesso liberado',
    error: 'Erro ao liberar acesso',
    step1: (name) => ({
      title: `Liberar o acesso de ${name}?`,
      content:
        'O usuário passa a ter acesso à plataforma como respondente. Deseja continuar?',
    }),
    step2: (name) => ({
      title: 'Confirmar liberação de acesso',
      content: `Confirma liberar definitivamente o acesso de ${name}? Esta é a segunda confirmação.`,
    }),
  },
  reject: {
    endpoint: (id) => `/user/${id}/reject`,
    redType: true,
    success: 'Cadastro excluído',
    error: 'Erro ao negar solicitação',
    step1: (name) => ({
      title: `Negar o acesso de ${name}?`,
      content:
        'O cadastro será excluído definitivamente (não fica registro). Deseja continuar?',
    }),
    step2: (name) => ({
      title: 'Confirmar exclusão do cadastro',
      content: `Confirma excluir definitivamente o cadastro de ${name}? Esta ação não pode ser desfeita. Esta é a segunda confirmação.`,
    }),
  },
};

export default function AccessRequests() {
  const { data, isLoading, mutate } = useSWR('/user/access-requests', fetchData);
  const requests = Array.isArray(data) ? data : [];
  const toast = useToast();

  // { type: 'approve' | 'reject', req, step: 1 | 2 } | null
  const [action, setAction] = useState(null);
  const [processing, setProcessing] = useState(false);

  function closeAction() {
    if (processing) return;
    setAction(null);
  }

  async function handleConfirm() {
    if (!action) return;
    if (action.step === 1) {
      setAction({ ...action, step: 2 });
      return;
    }
    const cfg = COPY[action.type];
    setProcessing(true);
    try {
      await baseService.patch(cfg.endpoint(action.req.id_user));
      toast({ title: cfg.success, status: 'success' });
      setAction(null);
      mutate();
    } catch (err) {
      toast({ title: cfg.error, status: 'error' });
    } finally {
      setProcessing(false);
    }
  }

  const cfg = action ? COPY[action.type] : null;
  const modalCopy =
    action && cfg
      ? cfg[action.step === 1 ? 'step1' : 'step2'](action.req.name || 'este usuário')
      : null;

  if (isLoading) return <Loader />;

  return (
    <MainContent>
      <PageHeaderStructure
        title='Solicitações de acesso'
        subtitle='Clientes que solicitaram acesso à plataforma nos últimos 30 dias e aguardam liberação.'
      />

      <Box bg='white' borderRadius='12px' mt='32px' p='8px' overflowX='auto'>
        {requests.length === 0 ? (
          <Flex align='center' justify='center' minH='220px' direction='column' gap='8px'>
            <Text fontWeight={600} color='#5C6066'>
              Nenhuma solicitação recente
            </Text>
            <Text fontSize='sm' color='#9CA0A6'>
              Solicitações de acesso pendentes dos últimos 30 dias aparecem aqui.
            </Text>
          </Flex>
        ) : (
          <Table variant='simple'>
            <Thead>
              <Tr>
                <Th>Empresa</Th>
                <Th>Nome</Th>
                <Th>Telefone (empresa)</Th>
                <Th>Solicitação</Th>
                <Th>Ações</Th>
              </Tr>
            </Thead>
            <Tbody>
              {requests.map((req) => (
                <Tr key={req.id_user}>
                  <Td fontWeight={600}>{req.company || '—'}</Td>
                  <Td>{req.name || '—'}</Td>
                  <Td>{req.phone || '—'}</Td>
                  <Td>
                    <Badge
                      bg='#FEF3D7'
                      color='#946200'
                      borderRadius='6px'
                      px='8px'
                      py='2px'
                      fontWeight={600}
                    >
                      {formatDateTime(req.requested_at)}
                    </Badge>
                  </Td>
                  <Td>
                    <Flex gap='8px'>
                      <Button
                        size='sm'
                        colorScheme='green'
                        onClick={() => setAction({ type: 'approve', req, step: 1 })}
                      >
                        Aceitar
                      </Button>
                      <Button
                        size='sm'
                        variant='outline'
                        colorScheme='red'
                        onClick={() => setAction({ type: 'reject', req, step: 1 })}
                      >
                        Negar
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      <AttentionModal
        isOpen={!!action}
        onClose={closeAction}
        submit={handleConfirm}
        isLoading={processing}
        redType={cfg?.redType}
        title={modalCopy?.title || ''}
        content={modalCopy?.content}
      />
    </MainContent>
  );
}
